import {
  CaretDown,
  CaretRight,
  CollapseAll,
  ExpandAll,
  Launch,
} from '@carbon/react/icons';
import { LifecycleStatusTag } from 'components/lifecycle-status-tag';
import { Button, Checkbox } from '@carbon/react';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import { ValidityStatusIndicator } from 'components/structure-grids/validity-status-indicator';
import React, { useEffect, useState } from 'react';
import { Grid, StructureGridCollection } from 'types/api-responses';
import { format0 } from 'utils/number';
import styles from './structure-grid-collections-list.module.scss';
import { sortCollections } from './utils';

// Special value to indicate "No Structure Grid" selection
export const NO_STRUCTURE_GRID_ID = -1;

interface SubsurfaceStructureGridCollectionsProps {
  collections: StructureGridCollection[];
  individualGrids?: Grid[];
  onSelectionChange: (checkedIds: number[]) => void;
  selectedCollectionId?: number | null;
  onCollectionChange?: (collectionId: number) => void;
  disableIndividualGrids?: boolean;
}

const SubsurfaceStructureGridCollections: React.FC<
  SubsurfaceStructureGridCollectionsProps
> = ({
  collections,
  individualGrids = [],
  onSelectionChange,
  selectedCollectionId: controlledSelectedId,
  onCollectionChange,
  disableIndividualGrids = false,
}) => {
  // Use controlled value if provided, otherwise manage internally
  const [internalSelectedId, setInternalSelectedId] = useState<number | null>(
    controlledSelectedId ??
      collections.find((c) => c.isDefault)?.collectionId ??
      null
  );
  const selectedCollectionId = controlledSelectedId ?? internalSelectedId;
  const [openIndexes, setOpenIndexes] = useState<{ [id: number]: boolean }>({});

  // Track which individual grids are checked
  const [checkedIndividualGrids, setCheckedIndividualGrids] = useState<{
    [gridId: number]: boolean;
  }>({});

  // Sync internal state with controlled value
  useEffect(() => {
    if (controlledSelectedId !== undefined) {
      setInternalSelectedId(controlledSelectedId);
    }
  }, [controlledSelectedId]);

  const handleRadioSelect = (collectionId: number) => {
    if (onCollectionChange) {
      onCollectionChange(collectionId);
    } else {
      setInternalSelectedId(collectionId);
    }
  };

  const handleToggle = (id: number) => {
    setOpenIndexes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleIndividualGridCheck = (gridId: number, checked: boolean) => {
    // Update checked state
    const newCheckedState = {
      ...checkedIndividualGrids,
      [gridId]: checked,
    };
    setCheckedIndividualGrids(newCheckedState);

    // Calculate and notify parent of new selection (only checked individual grids)
    const selectedIds: number[] = [];
    Object.entries(newCheckedState).forEach(([id, isChecked]) => {
      if (isChecked) {
        const gId = parseInt(id, 10);
        const grid = individualGrids.find((g) => g.gridId === gId);
        if (grid && grid.intersects) {
          selectedIds.push(gId);
        }
      }
    });
    onSelectionChange(selectedIds);
  };

  // Check if an individual grid is already in the selected collection
  const isGridInSelectedCollection = (gridId: number): boolean => {
    if (selectedCollectionId === null) return false;
    const selectedCollection = collections.find(
      (col) => col.collectionId === selectedCollectionId
    );
    if (!selectedCollection) return false;
    return selectedCollection.grids.some((grid) => grid.gridId === gridId);
  };

  if (collections.length === 0 && individualGrids.length === 0) {
    return (
      <div className={styles.emptyMessage}>
        No Structure Grid Collections Available
      </div>
    );
  }

  return (
    <div>
      {/* Collections Section */}
      {collections.length > 0 && (
        <div className={styles.collectionsSection}>
          <div className={styles.collectionActions}>
            <span className={styles.collectionTitle}>Collections</span>
            <span>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={CollapseAll}
                iconDescription="Collapse all"
                className={styles.collapseAllButton}
                onClick={() => {
                  setOpenIndexes({});
                }}
              />
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={ExpandAll}
                iconDescription="Expand all"
                className={styles.expandAllButton}
                onClick={() => {
                  const allOpen: { [id: number]: boolean } = {};
                  collections.forEach((col) => {
                    allOpen[col.collectionId] = true;
                  });
                  setOpenIndexes(allOpen);
                }}
              />
            </span>
          </div>

          {/* None option - at the top */}
          <div className={styles.collectionItem}>
            <div className={styles.headerRow} role="presentation">
              <span
                className={`${styles.headerLeft} ${styles.noneOptionHeaderLeft}`}
              >
                <span
                  className={`${styles.collectionName} ${styles.noneOptionLabel}`}
                >
                  None
                </span>
              </span>
              <span className={styles.headerRight}>
                <div
                  className={styles.radioWrapper}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRadioSelect(NO_STRUCTURE_GRID_ID);
                  }}
                >
                  <input
                    type="radio"
                    id="collection-radio-none"
                    name="structure-grid-collection-radio"
                    value={NO_STRUCTURE_GRID_ID.toString()}
                    checked={selectedCollectionId === NO_STRUCTURE_GRID_ID}
                    onChange={() => handleRadioSelect(NO_STRUCTURE_GRID_ID)}
                    className={styles.radioInput}
                  />
                </div>
              </span>
            </div>
          </div>

          {sortCollections(collections).map((collection) => {
            const isOpen = !!openIndexes[collection.collectionId];
            const intersectingGridIds = collection.grids
              .filter((grid) => grid.intersects)
              .map((grid) => grid.gridId);
            const isSelected = selectedCollectionId === collection.collectionId;

            return (
              <div
                key={collection.collectionId}
                className={styles.collectionItem}
              >
                <div className={styles.headerRow} role="presentation">
                  <span className={styles.headerLeft}>
                    <span
                      className={styles.caret}
                      role="button"
                      onClick={() => handleToggle(collection.collectionId)}
                    >
                      {isOpen ? <CaretDown /> : <CaretRight />}
                    </span>
                    <span className={styles.collectionName}>
                      {collection.collectionName} (
                      {format0(intersectingGridIds.length)})
                    </span>
                    {collection.isDefault && (
                      <FloatingTooltip
                        content="This Structure Grid Collection is the default reference for this area"
                        placement="top"
                      >
                        <LifecycleStatusTag
                          status="default"
                          className={styles.defaultTag}
                        />
                      </FloatingTooltip>
                    )}
                    <ValidityStatusIndicator status={collection.validity} />
                  </span>
                  <span className={styles.headerRight}>
                    <Button
                      kind="ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={Launch}
                      iconDescription="Open in new tab"
                      onClick={() => {
                        window.open(
                          `/structure_grid_collections/${collection.collectionId}`,
                          '_blank',
                          'noopener,noreferrer'
                        );
                      }}
                      className={styles.newTabButton}
                    />
                    <div
                      className={styles.radioWrapper}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRadioSelect(collection.collectionId);
                      }}
                    >
                      <input
                        type="radio"
                        id={`collection-radio-${collection.collectionId}`}
                        name="structure-grid-collection-radio"
                        value={collection.collectionId.toString()}
                        checked={isSelected}
                        onChange={() =>
                          handleRadioSelect(collection.collectionId)
                        }
                        className={styles.radioInput}
                      />
                    </div>
                  </span>
                </div>
                {isOpen && (
                  <div className={styles.collectionContent}>
                    {collection.grids.map((grid) => (
                      <div
                        key={`${collection.collectionId}-${grid.gridId}`}
                        className={styles.gridItemReadOnly}
                      >
                        <span
                          className={styles.gridColorSwatch}
                          style={{ backgroundColor: grid.displayColor }}
                        />
                        <span className={styles.gridItemName}>
                          <span className={styles.gridItemLabel}>
                            {grid.geologicInterval}
                          </span>
                          <span className={styles.gridItemSource}>
                            {grid.gridSurface} - Source: {grid.source}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Individual Structure Grid Surfaces Section */}
      {individualGrids.length > 0 && (
        <div className={styles.individualGridsSection}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>
              Individual Structure Grid Surfaces
            </span>
          </div>
          {individualGrids.map((grid) => {
            const isInSelectedCollection = isGridInSelectedCollection(
              grid.gridId
            );
            const isChecked =
              isInSelectedCollection || !!checkedIndividualGrids[grid.gridId];

            return (
              <div
                key={`individual-${grid.gridId}`}
                className={styles.individualGridItem}
              >
                <span
                  className={styles.gridColorSwatch}
                  style={{ backgroundColor: grid.displayColor }}
                />
                <span className={styles.gridItemName}>
                  <span className={styles.gridItemLabel}>
                    {grid.geologicInterval}
                  </span>
                  <span className={styles.gridItemSource}>
                    {grid.gridSurface} - Source: {grid.source}
                  </span>
                </span>
                <Checkbox
                  id={`individual-grid-${grid.gridId}`}
                  checked={isChecked}
                  disabled={
                    disableIndividualGrids ||
                    !grid.intersects ||
                    isInSelectedCollection
                  }
                  onChange={(_evt, { checked }: { checked: boolean }) => {
                    handleIndividualGridCheck(grid.gridId, checked);
                  }}
                  labelText=""
                  className={styles.individualGridCheckbox}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubsurfaceStructureGridCollections;
